import axios from 'axios';
import { ApiException } from 'src/api/FiretrolApiClient';
import { parse422Errors } from 'src/api/utils/errorUtils';

const DEFAULT_FALLBACK = 'An unexpected error occurred.';

// Resolves a user-facing message from the shapes the app actually throws:
// ApiException (NSwag), parsed problem-details, AxiosError, Error, or string.
export const getErrorMessage = (
  err: unknown,
  fallback = DEFAULT_FALLBACK,
): string => {
  const resolved = resolve(err);
  const trimmed = resolved?.trim();
  return trimmed && trimmed.length ? trimmed : fallback;
};

const resolve = (err: unknown): string | undefined => {
  const validation = parse422Errors(err);
  if (validation && validation.messages.length) {
    return validation.messages.join('\n');
  }

  if (err == null) return undefined;

  if (ApiException.isApiException(err)) {
    // The generated client hardcodes "An unexpected server error occurred."
    // for every non-success status, so the server's own message — the only
    // useful text for a refused 409 — has to win over err.message.
    return messageFromResponseBody(err.response) ?? err.message;
  }

  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string') {
      return messageFromResponseBody(data) ?? err.message;
    }
    return data?.message ?? err.message;
  }

  if (err instanceof Error) return err.message;

  if (typeof err === 'string') return err;

  return undefined;
};

const messageFromResponseBody = (response: unknown): string | undefined => {
  if (typeof response !== 'string' || !response.trim().length) return undefined;

  try {
    const parsed = JSON.parse(response);
    return typeof parsed?.message === 'string' ? parsed.message : undefined;
  } catch {
    return undefined;
  }
};
